import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Typography,
  Skeleton,
  Grid,
  Box,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Button,
  Chip,
  Paper,
} from '@mui/material';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import Layout from '../components/Layout/Layout';
import SearchComponet from '../components/search/Search';
import { getPropertyRequestsList, getLookup } from '../network/apis';
import PropertyRequestsTable from '../sections/@dashboard/propertyRequests/DataTable';
import { ReactComponent as DropdownIcon } from '../theme/images/deopdownIcon.svg';

export default function PropertyRequests() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [sortType, setSortType] = useState('DESC');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState({ content: [], totalPages: 0, totalElements: 0 });

  // Lookups state
  const [areaLookups, setAreaLookups] = useState([]);
  const [budgetLookups, setBudgetLookups] = useState([]);
  const [roomCountLookups, setRoomCountLookups] = useState([]);

  // Fetch Lookups for REQUIRED_AREA and REQUEST_BUDGET
  useEffect(() => {
    let isMounted = true;
    getLookup('REQUIRED_AREA,REQUEST_BUDGET,REQUIRED_ROOM_COUNT')
      .then((res) => {
        if (!isMounted) return;
        const data = res?.data || {};
        if (data.REQUIRED_AREA) {
          setAreaLookups(data.REQUIRED_AREA);
        }
        if (data.REQUEST_BUDGET) {
          setBudgetLookups(data.REQUEST_BUDGET);
        }
        if (data.REQUIRED_ROOM_COUNT) {
          setRoomCountLookups(data.REQUIRED_ROOM_COUNT);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch lookups:', err);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Compute lookup dictionaries for fast label lookup
  const areaLookupsMap = useMemo(() => {
    const map = {};
    areaLookups.forEach((item) => {
      map[item.value] = isRtl ? item.labelAr || item.label : item.labelEn || item.label;
    });
    return map;
  }, [areaLookups, isRtl]);

  const budgetLookupsMap = useMemo(() => {
    const map = {};
    budgetLookups.forEach((item) => {
      map[item.value] = isRtl ? item.labelAr || item.label : item.labelEn || item.label;
    });
    return map;
  }, [budgetLookups, isRtl]);

  const roomCountLookupsMap = useMemo(() => {
    const map = {};
    roomCountLookups.forEach((item) => {
      map[item.value] = isRtl ? item.labelAr || item.label : item.labelEn || item.label;
    });
    return map;
  }, [roomCountLookups, isRtl]);

  // Fetch Property Requests
  const fetchPropertyRequests = useCallback(
    (currentPage, currentKeyword, currentStatus, currentSortType) => {
      setLoading(true);
      getPropertyRequestsList(currentPage, currentKeyword, currentStatus, currentSortType, 10)
        .then((res) => {
          setRequests(res?.data || { content: [], totalPages: 0, totalElements: 0 });
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching property requests:', err);
          setLoading(false);
        });
    },
    []
  );

  useEffect(() => {
    fetchPropertyRequests(page, search, status, sortType);
  }, [page, search, status, sortType, fetchPropertyRequests]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage - 1);
  };

  const handleSearch = (query) => {
    setSearch(query);
    setPage(0);
  };

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
    setPage(0);
  };

  const handleSortChange = (newSort) => {
    setSortType(newSort);
    setPage(0);
  };

  const handleResetFilters = () => {
    setSearch('');
    setStatus('');
    setSortType('ASC');
    setPage(0);
  };

  const hasActiveFilters = search !== '' || status !== '' || sortType !== 'DESC';

  return (
    <>
      <Helmet>
        <title>{t('propertyRequests.title')}</title>
      </Helmet>

      <Layout>
        {/* Page Title & Count Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography
              sx={{
                fontSize: '24px',
                fontWeight: '700',
                lineHeight: '32px',
                color: '#111827',
              }}
            >
              {t('propertyRequests.title')}
            </Typography>
            {requests?.totalElements !== undefined && (
              <Chip
                label={`${requests.totalElements} ${t('propertyRequests.totalRequests')}`}
                size="small"
                sx={{
                  backgroundColor: '#E0F2FE',
                  color: '#0369A1',
                  fontWeight: 700,
                  fontSize: '12px',
                  borderRadius: '8px',
                }}
              />
            )}
          </Box>
        </Box>

        {/* Filter & Search Bar */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid #E2E8F0',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Search Input */}
            <Grid item xs={12} md={5}>
              <SearchComponet
                onSearch={handleSearch}
                label={t('propertyRequests.searchPlaceholder')}
                style={{ width: '100%' }}
              />
            </Grid>

            {/* Status Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="medium" sx={{ direction: isRtl ? 'rtl' : 'ltr' }}>
                <InputLabel
                  id="status-type-label"
                  sx={{
                    left: isRtl ? 'auto' : 14,
                    right: isRtl ? 32 : 'auto',
                    transformOrigin: isRtl ? 'top right' : 'top left',
                    textAlign: isRtl ? 'right' : 'left',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(0, -9px) scale(0.75)',
                    },
                  }}
                >
                  {t('propertyRequests.filterByStatus')}
                </InputLabel>
                <Select
                  dir={isRtl ? 'rtl' : 'ltr'}
                  labelId="status-type-label"
                  id="status-filter-select"
                  value={status}
                  label={t('propertyRequests.filterByStatus')}
                  onChange={handleStatusChange}
                  sx={{
                    borderRadius: '10px',
                    textAlign: isRtl ? 'right' : 'left',
                    '& .MuiSelect-select': {
                      paddingRight: isRtl ? '14px !important' : '36px !important',
                      paddingLeft: isRtl ? '36px !important' : '14px !important',
                      textAlign: isRtl ? 'right' : 'left',
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiSelect-icon': {
                      right: isRtl ? 'unset !important' : '12px !important',
                      left: isRtl ? '12px !important' : 'unset !important',
                      position: 'absolute',
                      pointerEvents: 'none',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      textAlign: isRtl ? 'right' : 'left',
                    },
                  }}
                  IconComponent={() => <DropdownIcon style={{ width: '15px', height: '15px' }} />}
                >
                  <MenuItem
                    value=""
                    sx={{
                      direction: isRtl ? 'rtl' : 'ltr',
                      justifyContent: isRtl ? 'flex-end' : 'flex-start',
                      textAlign: isRtl ? 'right' : 'left',
                    }}
                  >
                    {t('propertyRequests.all')}
                  </MenuItem>
                  <MenuItem
                    value="COMPLETED"
                    sx={{
                      direction: isRtl ? 'rtl' : 'ltr',
                      justifyContent: isRtl ? 'flex-end' : 'flex-start',
                      textAlign: isRtl ? 'right' : 'left',
                    }}
                  >
                    {t('propertyRequests.completed')}
                  </MenuItem>
                  <MenuItem
                    value="PENDING"
                    sx={{
                      direction: isRtl ? 'rtl' : 'ltr',
                      justifyContent: isRtl ? 'flex-end' : 'flex-start',
                      textAlign: isRtl ? 'right' : 'left',
                    }}
                  >
                    {t('propertyRequests.pending')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Sort Type Filter */}
            <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="medium" sx={{ direction: isRtl ? 'rtl' : 'ltr' }}>
                <InputLabel
                  id="sort-type-label"
                  sx={{
                    left: isRtl ? 'auto' : 14,
                    right: isRtl ? 32 : 'auto',
                    transformOrigin: isRtl ? 'top right' : 'top left',
                    textAlign: isRtl ? 'right' : 'left',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(0, -9px) scale(0.75)',
                    },
                  }}
                >
                  {t('propertyRequests.sortType')}
                </InputLabel>

                <Select
                  labelId="sort-type-label"
                  id="sort-type-select"
                  value={sortType}
                  label={t('propertyRequests.sortType')}
                  onChange={(e) => handleSortChange(e.target.value)}
                  dir={isRtl ? 'rtl' : 'ltr'}
                  IconComponent={() => <DropdownIcon style={{ width: '15px', height: '15px' }} />}
                  sx={{
                    borderRadius: '10px',
                    textAlign: isRtl ? 'right' : 'left',
                    '& .MuiSelect-select': {
                      paddingRight: isRtl ? '14px !important' : '36px !important',
                      paddingLeft: isRtl ? '36px !important' : '14px !important',
                      textAlign: isRtl ? 'right' : 'left',
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiSelect-icon': {
                      right: isRtl ? 'unset !important' : '12px !important',
                      left: isRtl ? '12px !important' : 'unset !important',
                      position: 'absolute',
                      pointerEvents: 'none',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      textAlign: isRtl ? 'right' : 'left',
                    },
                  }}
                >
                  <MenuItem
                    value="ASC"
                    sx={{
                      direction: isRtl ? 'rtl' : 'ltr',
                      justifyContent: isRtl ? 'flex-end' : 'flex-start',
                      textAlign: isRtl ? 'right' : 'left',
                    }}
                  >
                    {t('propertyRequests.asc')}
                  </MenuItem>
                  <MenuItem
                    value="DESC"
                    sx={{
                      direction: isRtl ? 'rtl' : 'ltr',
                      justifyContent: isRtl ? 'flex-end' : 'flex-start',
                      textAlign: isRtl ? 'right' : 'left',
                    }}
                  >
                    {t('propertyRequests.desc')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Reset Button */}
            {hasActiveFilters && (
              <Grid item xs={12} sm={12} md={1.5}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="inherit"
                  onClick={handleResetFilters}
                  startIcon={<FilterAltOffOutlinedIcon />}
                  sx={{
                    height: '54px',
                    borderRadius: '10px',
                    borderColor: '#CBD5E1',
                    color: '#64748B',
                    '&:hover': {
                      borderColor: '#94A3B8',
                      backgroundColor: '#F8FAFC',
                    },
                  }}
                >
                  {t('propertyRequests.resetFilters')}
                </Button>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Content / Table / Skeleton */}
        {loading ? (
          <Stack spacing={1.5}>
            <Skeleton variant="rounded" height={60} sx={{ borderRadius: '12px' }} />
            <Skeleton variant="rounded" height={70} sx={{ borderRadius: '12px' }} />
            <Skeleton variant="rounded" height={70} sx={{ borderRadius: '12px' }} />
            <Skeleton variant="rounded" height={70} sx={{ borderRadius: '12px' }} />
            <Skeleton variant="rounded" height={70} sx={{ borderRadius: '12px' }} />
            <Skeleton variant="rounded" height={70} sx={{ borderRadius: '12px' }} />
          </Stack>
        ) : (
          <PropertyRequestsTable
            requests={requests}
            handleChangePage={handleChangePage}
            page={page}
            rowsPerPage={10}
            areaLookupsMap={areaLookupsMap}
            budgetLookupsMap={budgetLookupsMap}
            roomCountLookupsMap={roomCountLookupsMap}
            sortType={sortType}
            onSortChange={handleSortChange}
            onStatusUpdated={() => fetchPropertyRequests(page, search, status, sortType)}
          />
        )}
      </Layout>
    </>
  );
}
